import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { albumsApi } from '../api/albums';
import { authApi } from '../api/auth';
import { AlbumPreview } from '../types';
import { MachhaCharacter, getMachhaAsset } from '../lib/machhaAsset';
import { Button } from '../components/UI/Button';
import { track, EVENTS } from '../lib/mixpanel';
import styles from './AlbumPreviewPage.module.css';

type PageState =
  | { phase: 'loading' }
  | { phase: 'invalid' }
  | { phase: 'preview'; preview: AlbumPreview }
  | { phase: 'joining' }
  | { phase: 'kicked' }
  | { phase: 'domain-restricted'; allowedEmailDomains: string[] };

export function AlbumPreviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, initialized } = useAuthStore();

  const [pageState, setPageState] = useState<PageState>({ phase: 'loading' });

  // Step 1: Load public preview (no auth)
  useEffect(() => {
    if (!token) {
      setPageState({ phase: 'invalid' });
      return;
    }
    albumsApi
      .getPreview(token)
      .then((preview) => {
        setPageState({ phase: 'preview', preview });
        track(EVENTS.ALBUM_PREVIEW_VIEWED, { album_id: preview.album._id, album_name: preview.album.name });
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setPageState({ phase: 'invalid' });
        } else {
          setPageState({ phase: 'invalid' });
        }
      });
  }, [token]);

  // Step 2: User explicitly taps "Join Album" — called from the CTA button
  const handleJoin = async () => {
    if (!user) {
      track(EVENTS.ALBUM_JOIN_STARTED, { token, source: 'cta_button' });
      window.location.href = authApi.googleLoginUrl(`/join/${token}`);
      return;
    }
    if (!token) return;
    setPageState({ phase: 'joining' });
    track(EVENTS.ALBUM_JOIN_STARTED, { token });
    try {
      const result = await albumsApi.joinAlbum(token);
      track(EVENTS.ALBUM_JOINED, { album_id: result.albumId });
      const suffix = result.joined ? '?justJoined=1' : '';
      navigate(`/albums/${result.albumId}${suffix}`, { replace: true });
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { code?: string; allowedEmailDomains?: string[] } } })?.response;
      if (res?.status === 403 && res.data?.code === 'DOMAIN_RESTRICTED') {
        track(EVENTS.ALBUM_JOIN_BLOCKED_DOMAIN, { token });
        setPageState({ phase: 'domain-restricted', allowedEmailDomains: res.data.allowedEmailDomains ?? [] });
      } else if (res?.status === 403) {
        setPageState({ phase: 'kicked' });
      } else {
        navigate('/albums', { replace: true });
      }
    }
  };

  // Restricted-access screen action: drop the current session and let the guest
  // pick the right organization account, returning to this same join link.
  const handleSwitchAccount = async () => {
    if (!token) return;
    try {
      await authApi.logout();
    } catch {
      /* even if logout fails, still route to Google to re-authenticate */
    }
    window.location.href = authApi.googleLoginUrl(`/join/${token}`);
  };

  // ── Render states ─────────────────────────────────────────────────────────

  if (pageState.phase === 'loading' || !initialized) {
    return (
      <div className={styles['fullscreen-center']}>
        <div className={styles['spinner']} />
      </div>
    );
  }

  if (pageState.phase === 'joining') {
    return (
      <div className={styles['fullscreen-center-col']}>
        <div className={styles['spinner']} />
        <p className={styles['joining-text']}>Joining album…</p>
      </div>
    );
  }


  if (pageState.phase === 'invalid') {
    return (
      <div className={styles['fullscreen-center-text']}>
        <MachhaCharacter state="Not Found" size={200} />
        <h1 className={styles['state-title']}>
          Invalid invite link
        </h1>
        <p className={styles['state-description']}>
          This album link is invalid or has been removed by the creator.
        </p>
      </div>
    );
  }

  if (pageState.phase === 'kicked') {
    return (
      <div className={styles['fullscreen-center-text']}>
        <MachhaCharacter state="Forbidden" size={200} />
        <h1 className={styles['state-title']}>
          Access removed
        </h1>
        <p className={styles['state-description']}>
          You've been removed from this album by the creator.
        </p>
      </div>
    );
  }

  if (pageState.phase === 'domain-restricted') {
    const domains = pageState.allowedEmailDomains;
    const domainList = domains.map((d) => `@${d}`).join(domains.length === 2 ? ' or ' : ', ');
    return (
      <div className={styles['fullscreen-center-text']}>
        <MachhaCharacter state="Forbidden" size={200} />
        <h1 className={styles['state-title']}>Restricted access</h1>
        <p className={styles['state-description']}>
          This album is limited to members of a specific organization. To view the photos,
          please sign in with your work email{domains.length > 0 ? (
            <> ending in <strong>{domainList}</strong></>
          ) : null}.
        </p>
        {user?.email && (
          <p className={styles['state-description']} style={{ marginTop: '-0.25rem', opacity: 0.75 }}>
            You're currently signed in as <strong>{user.email}</strong>.
          </p>
        )}
        <div style={{ marginTop: '0.5rem' }}>
          <Button onClick={handleSwitchAccount} variant="primary">
            Sign in with a different email
          </Button>
        </div>
      </div>
    );
  }

  // phase === 'preview' and user is NOT logged in — show blurred preview + CTA
  const { preview } = pageState;
  const { coverThumbnailUrl } = preview.album;
  const thumbnails = preview.previewThumbnails.slice(0, 3);
  const cardRotations = [-5, 0, 5];

  return (
    <div className={styles['preview-page']}>
      <motion.div
        className={styles['preview-content']}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      >
        {/* Cover image or fan */}
        <div className={styles['cover-section']}>
          {coverThumbnailUrl ? (
            /* Blurred cover hero */
            <div className={styles['cover-hero']}>
              <img
                src={coverThumbnailUrl}
                alt=""
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                className={styles['cover-image']}
              />
              {/* Album name overlay on cover */}
              <div className={styles['cover-overlay']}>
                <h1 className={styles['cover-title']}>
                  {preview.album.name}
                </h1>
                <p className={styles['cover-meta']}>
                  {preview.album.memberCount} member{preview.album.memberCount !== 1 ? 's' : ''} · {preview.album.photoCount} photo{preview.album.photoCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Album name (above fan when no cover) */}
              <h1 className={styles['no-cover-title']}>
                {preview.album.name}
              </h1>
              <p className={styles['no-cover-meta']}>
                {preview.album.memberCount} member{preview.album.memberCount !== 1 ? 's' : ''} · {preview.album.photoCount} photo{preview.album.photoCount !== 1 ? 's' : ''}
              </p>
              {/* Blurred thumbnail fan */}
              <div className={styles['fan-container']}>
                {thumbnails.length > 0 ? (
                  thumbnails.map((url, i) => (
                    <div
                      key={i}
                      className={styles['fan-card']}
                      style={{
                        transform: `rotate(${cardRotations[i] ?? 0}deg) translateX(${(i - 1) * 20}px)`,
                        zIndex: i === 1 ? 3 : i === 0 ? 2 : 1,
                      }}
                    >
                      <img
                        src={url}
                        alt=""
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        className={styles['fan-card-image']}
                      />
                    </div>
                  ))
                ) : (
                  <MachhaCharacter state="Onboarding" size={220} />
                )}
              </div>
            </>
          )}
        </div>

        {/* Show album name+stats below when cover hero is used */}
        {coverThumbnailUrl && (
          <>
            <h1 className={styles['below-cover-title']}>
              {preview.album.name}
            </h1>
            <p className={styles['below-cover-meta']}>
              {preview.album.memberCount} member{preview.album.memberCount !== 1 ? 's' : ''} · {preview.album.photoCount} photo{preview.album.photoCount !== 1 ? 's' : ''}
            </p>
          </>
        )}

        {/* Access restriction note — shown upfront so guests know which email to use */}
        {preview.album.allowedEmailDomains && preview.album.allowedEmailDomains.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
              padding: '10px 14px',
              borderRadius: 'var(--radius-clay-md)',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.18)',
              color: 'var(--text-secondary)',
              fontSize: 13,
              lineHeight: 1.4,
              maxWidth: 360,
            }}
          >
            <ShieldCheck size={16} style={{ color: 'var(--clay-orange)', flexShrink: 0 }} />
            <span>
              Restricted album — sign in with your{' '}
              <strong style={{ color: 'var(--text-primary)' }}>
                {preview.album.allowedEmailDomains.map((d) => `@${d}`).join(' or ')}
              </strong>{' '}
              email.
            </span>
          </div>
        )}

        {/* CTA */}
        <Button onClick={handleJoin} variant="primary">
          {user ? (
            'Join Album'
          ) : (
            <>
              <img
                src={getMachhaAsset('LandingPageAssets', 'cta-google-logo-clay')}
                alt=""
                width={20}
                height={20}
                className={styles['cta-google-icon']}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              Join with Google
            </>
          )}
        </Button>

        <p className={styles['cta-hint']}>
          {user
            ? 'Tap to join and start viewing photos'
            : 'Sign in to view photos from this album shared with you'}
        </p>
      </motion.div>
    </div>
  );
}
